import api from 'api';
import { actions } from 'store';
import { useDispatch, useReduxState } from 'hooks';
import { ModalButtonWithForm } from 'components/common';

import { FormGroup, RadioGroup, Text } from 'components/common/form';

interface Props {
  disabled?: boolean;
  hasMultipleCompletions: boolean;
  updateUsers: () => Promise<void>;
  userId: number;
}

const AchievementCompletionsRemove = ({
  disabled = false,
  hasMultipleCompletions,
  updateUsers,
  userId
}: Props) => {
  const { dispatch } = useDispatch();
  const {
    messages,
    pageState: { achievement },
    serverConfig: { requireReasonWhenDeletingData }
  } = useReduxState();

  const remove = async (values: {
    removeAllCompletions: boolean;
    deletionReason?: string;
  }) => {
    const params = new URLSearchParams({
      removeAllCompletions: String(values.removeAllCompletions),
      ...(values.deletionReason && { deletionReason: values.deletionReason })
    });

    await api.delete(
      `/admin/achievements/${
        achievement.id
      }/completions/${userId}?${params.toString()}`
    );
    await updateUsers();
    dispatch(
      actions.addNotice({
        text:
          values.removeAllCompletions || !hasMultipleCompletions
            ? messages.commonRemoveUserSuccess
            : messages.eventCalendarAchievementRemoveCompletionSuccess,
        type: 'success'
      })
    );
  };

  return (
    <ModalButtonWithForm
      buttonCategory="lighter"
      buttonDisabled={disabled}
      buttonIcon="trash-alt"
      buttonIsDropdown={true}
      buttonProps={
        disabled
          ? { tooltip: messages.achievementUsersRemoveDisabled }
          : undefined
      }
      buttonSize="header"
      buttonText={messages.commonRemoveUser}
      initialValues={{ removeAllCompletions: false }}
      modalActionCategory="danger"
      modalActionText={messages.commonRemove}
      modalTitle={messages.commonRemoveUser}
      onSubmit={remove}
    >
      {() => (
        <>
          {hasMultipleCompletions ? (
            <FormGroup label={messages.achievementUsersRemoveOneOrMultiple}>
              <RadioGroup
                field="removeAllCompletions"
                id={`achievement-users-remove-all-completions-${userId || 0}`}
                options={[
                  {
                    id: 'achievement-users-remove-all-completions-false',
                    label: messages.achievementUsersRemoveOne,
                    value: false
                  },
                  {
                    id: 'achievement-users-remove-all-completions-true',
                    label: messages.achievementUsersRemoveMultiple,
                    value: true
                  }
                ]}
              />
            </FormGroup>
          ) : (
            <>{messages.achievementUsersRemoveConfirm}</>
          )}

          {requireReasonWhenDeletingData ? (
            <FormGroup
              htmlFor={`deletionReason-achievement-${achievement?.id || 0}-${
                userId || 0
              }`}
              label={messages.commonDeletionReason}
              style={{ marginTop: 15 }}
            >
              <Text
                field="deletionReason"
                id={`deletionReason-achievement-${achievement?.id || 0}-${
                  userId || 0
                }`}
                maxLength={255}
                autocomplete="off"
                placeholder={messages.commonInputDeletionReason}
                required={true}
              />
            </FormGroup>
          ) : null}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementCompletionsRemove;
